import { Component, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  CommentsPostDisplayPaginationAllInOneComponent,
  ConversationApiService,
  ConversationCommentListQuery,
  ConversationDefinition,
  ConversationStateService,
  PaginatedComments,
} from '@frontend/shared';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../../auth';

const COMMENTS_PER_PAGE = 10;

/**
 * A delivery's own conversation: the private comments between the member who took the work and
 * staff, which only they see. The work screens of community inputs — a review, a video — show it
 * beside their instructions.
 *
 * It finds the conversation by the delivery, without creating one: the first comment creates it.
 * The conversation is in English, as the insight editor's is, so switching language never hides it.
 */
@Component({
  selector: 'multisite-delivery-conversation',
  template: `
    <comments-post-display-pagination-all-in-one
      [user]="user() ?? undefined"
      [hasPermissionToReadComments]="true"
      [hasPermissionToPostComments]="canPost()"
      [disabled]="!canPost()"
      [paginatedComments]="paginatedComments()"
      [loadingComments]="loadingComments()"
      [clearCommentForm]="clearCommentForm()"
      [titleTranslationKey]="titleTranslationKey()"
      [titleTranslationScope]="translationScope()"
      [descriptionTranslationKey]="descriptionTranslationKey()"
      [descriptionTranslationScope]="translationScope()"
      (postComment)="postComment($event)"
      (getComments)="getComments($event)">
    </comments-post-display-pagination-all-in-one>
  `,
  imports: [CommentsPostDisplayPaginationAllInOneComponent],
})
export class DeliveryConversationComponent {
  private authService = inject(AuthService);
  private conversationApi = inject(ConversationApiService);
  private conversationState = inject(ConversationStateService);
  private destroyRef = inject(DestroyRef);

  deliveryId = input.required<number>();
  /** False once the work is handed in, when the conversation is only read. */
  canPost = input(true);
  titleTranslationKey = input<string>();
  descriptionTranslationKey = input<string>();
  translationScope = input<string>();

  readonly user = toSignal(this.authService.user);

  paginatedComments = signal<PaginatedComments | undefined>(undefined);
  loadingComments = signal(false);
  clearCommentForm = signal(false);
  private conversationId = signal<number | null>(null);

  constructor() {
    effect(() => {
      this.deliveryId();

      untracked(() => {
        this.conversationId.set(null);
        this.paginatedComments.set(undefined);
        this.getComments(1);
      });
    });
  }

  getComments(page: number): void {
    const conversationId = this.conversationId();

    if (conversationId) {
      this.loadComments(conversationId, page);
      return;
    }

    this.previewConversation(page);
  }

  postComment(message: string): void {
    const conversationId = this.conversationId();
    const page = this.paginatedComments()?.meta.current_page ?? 1;

    if (conversationId) {
      this.conversationApi
        .createCommentInConversation(conversationId, message)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.clearCommentForm.update((value) => !value);
            this.getComments(page);
          },
        });
      return;
    }

    this.conversationState
      .submitFirstCommentByDefinition(this.definition(), message, this.commentQuery(page))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.conversationId.set(result.conversationId);
          this.paginatedComments.set(result.comments);
          this.clearCommentForm.update((value) => !value);
        },
      });
  }

  private previewConversation(page: number): void {
    this.loadingComments.set(true);

    this.conversationApi
      .previewConversation(this.definition())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (preview) => {
          const id = (preview as { id?: unknown })?.id;

          if (typeof id === 'number') {
            this.conversationId.set(id);
            this.loadComments(id, page);
          } else {
            this.paginatedComments.set(undefined);
            this.loadingComments.set(false);
          }
        },
        error: () => this.loadingComments.set(false),
      });
  }

  private loadComments(conversationId: number, page: number): void {
    this.loadingComments.set(true);

    this.conversationApi
      .listCommentsByConversationId(conversationId, this.commentQuery(page))
      .pipe(
        finalize(() => this.loadingComments.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({ next: (comments) => this.paginatedComments.set(comments) });
  }

  private definition(): ConversationDefinition {
    return { locale: 'en', category: null, type: null, deliveries: [this.deliveryId()] };
  }

  private commentQuery(page: number): ConversationCommentListQuery {
    return { page, per_page: COMMENTS_PER_PAGE, sort_by: 'created_at', sort_direction: 'asc' };
  }
}
